import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useCallback, useEffect, useSyncExternalStore } from "react";
import { createRoot, type Root } from "react-dom/client";

import { ApiClient, MemoryBearerToken } from "@decizyon/api-client";

import { OperatorAuthController, type OperatorTokenResponse } from "../auth/OperatorAuthController";
import { BrowserCredentialProvider } from "../auth/browserCredentials";
import { readOperatorIdFromToken } from "../auth/operatorIdentity";
import { createApiTicketBoardDataSource } from "../features/tickets/ticketBoardDataSource";
import type { TicketBoardDataSource } from "../features/tickets/ticketBoardTypes";
import { LoginScreen } from "../screens/LoginScreen";
import { IdentityBoundary } from "./IdentityBoundary";
import { resolveHashScreen, setHashScreen, useHashScreen } from "./useHashScreen";

const PrincipalScreen = lazy(async () => {
  const module = await import("../screens/PrincipalScreen");
  return { default: module.PrincipalScreen };
});

interface AppRuntime {
  readonly apiClient: ApiClient;
  readonly authController: OperatorAuthController;
  readonly bearerToken: MemoryBearerToken;
  readonly identityBoundary: IdentityBoundary;
  readonly operatorId: { value: string | null };
  readonly queryClient: QueryClient;
  readonly ticketBoardDataSource: TicketBoardDataSource;
  readonly tokenExpiresAt: { value: number };
}

function createRuntime(): AppRuntime {
  const apiBaseUrl = resolveApiBaseUrl();
  const realtimeUrl = resolveRealtimeUrl();
  const bearerToken = new MemoryBearerToken();
  const queryClient = new QueryClient({ defaultOptions: { queries: { retry: false, staleTime: 15_000 } } });
  const identityBoundary = new IdentityBoundary({ bearerToken, queryClient });
  const tokenExpiresAt = { value: 0 };
  const operatorId = { value: null as string | null };
  // `authController` is referenced inside `clearAndShowLogin` below before it
  // is assigned a few lines later. This is safe with `const` because the
  // closure only reads the binding when the handler actually runs (after
  // `createRuntime` has finished), never during construction itself.
  const clearAndShowLogin = (): void => {
    identityBoundary.clearIdentity();
    tokenExpiresAt.value = 0;
    operatorId.value = null;
    void authController.cancel();
    window.history.replaceState(null, "", "#login");
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  };

  const authController: OperatorAuthController = new OperatorAuthController({
    apiBaseUrl,
    credentialProvider: new BrowserCredentialProvider(),
    fetchImplementation: window.fetch.bind(window),
    onAuthenticated: (token: OperatorTokenResponse) => {
      bearerToken.set(token.token);
      operatorId.value = readOperatorIdFromToken(token.token);
      tokenExpiresAt.value = Date.now() + token.expiresIn * 1_000;
      setHashScreen("principal");
    },
  });
  const apiClient = new ApiClient({
    baseUrl: apiBaseUrl,
    bearerToken,
    isProtectedScopeAvailable: identityBoundary.isScopeAvailable,
    onForbidden: (protectedScope) => {
      if (protectedScope !== undefined) identityBoundary.revokeScope(protectedScope);
    },
    onUnauthorized: clearAndShowLogin,
    registerProtectedOperation: (protectedScope, cancel) => (
      identityBoundary.registerProtectedOperation(protectedScope, cancel)
    ),
  });
  const ticketBoardDataSource = createApiTicketBoardDataSource(apiClient, realtimeUrl);
  return {
    apiClient,
    authController,
    bearerToken,
    identityBoundary,
    operatorId,
    queryClient,
    ticketBoardDataSource,
    tokenExpiresAt,
  };
}

function resolveApiBaseUrl(): URL {
  const configured = import.meta.env.VITE_API_BASE_URL?.trim();
  return new URL(
    configured === undefined || configured.length === 0
      ? "/api/v1/"
      : configured.endsWith("/") ? configured : `${configured}/`,
    window.location.origin,
  );
}

function resolveRealtimeUrl(): URL | undefined {
  const configured = import.meta.env.VITE_REALTIME_URL?.trim();
  return configured === undefined || configured.length === 0 ? undefined : new URL(configured);
}

function App({ runtime }: { readonly runtime: AppRuntime }): JSX.Element {
  const clearIdentity = useCallback(() => {
    runtime.identityBoundary.clearIdentity();
    runtime.tokenExpiresAt.value = 0;
    runtime.operatorId.value = null;
    void runtime.authController.cancel();
  }, [runtime]);
  const registerSubscription = useCallback((scope: string, close: () => void) => (
    runtime.identityBoundary.registerSubscription(scope, close)
  ), [runtime]);
  const requestedScreen = useHashScreen(clearIdentity);
  const authState = useSyncExternalStore(runtime.authController.subscribe, runtime.authController.getSnapshot);
  const revokedScopes = useSyncExternalStore(
    runtime.identityBoundary.subscribe,
    runtime.identityBoundary.getSnapshot,
  );
  const hasValidBearer = runtime.bearerToken.hasToken && runtime.tokenExpiresAt.value > Date.now();
  const { screen } = resolveHashScreen(`#${requestedScreen}`, hasValidBearer);

  useEffect(() => {
    document.title = screen === "principal" ? "Chamados · Suporte Decizyon" : "Entrar · Suporte Decizyon";
    const revokeScope = (event: Event): void => {
      if (!(event instanceof CustomEvent) || typeof event.detail !== "object" || event.detail === null) return;
      const detail = event.detail as Readonly<Record<string, unknown>>;
      if (typeof detail.scope === "string") runtime.identityBoundary.revokeScope(detail.scope);
    };
    window.addEventListener("decizyon:scope-revoked", revokeScope);
    return () => window.removeEventListener("decizyon:scope-revoked", revokeScope);
  }, [runtime, screen]);

  useEffect(() => {
    if (requestedScreen !== "principal") return;
    const remaining = runtime.tokenExpiresAt.value - Date.now();
    if (!runtime.bearerToken.hasToken || remaining <= 0) {
      setHashScreen("login");
      return;
    }
    const timeout = window.setTimeout(() => {
      clearIdentity();
      window.history.replaceState(null, "", "#login");
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    }, remaining);
    return () => window.clearTimeout(timeout);
  }, [clearIdentity, hasValidBearer, requestedScreen, runtime]);

  if (requestedScreen === "principal" && !hasValidBearer) {
    return <></>;
  }
  if (screen === "principal") {
    return (
      <Suspense fallback={null}>
        <PrincipalScreen
          dataSource={runtime.ticketBoardDataSource}
          operatorId={runtime.operatorId.value}
          registerSubscription={registerSubscription}
          revokedScopes={revokedScopes}
        />
      </Suspense>
    );
  }
  return <LoginScreen visualState={authState.visualState} statusMessage={authState.message} onSubmit={(credentials) => runtime.authController.submit(credentials)} onSubmitTotp={(code) => runtime.authController.submitTotp(code)} />;
}

export function mountApp(container: HTMLElement): () => void {
  const runtime = createRuntime();
  const root: Root = createRoot(container);
  root.render(<QueryClientProvider client={runtime.queryClient}><App runtime={runtime} /></QueryClientProvider>);
  return () => {
    runtime.identityBoundary.clearIdentity();
    runtime.authController.destroy();
    root.unmount();
  };
}
